import type { DacVerdict } from "@ks-dac/shared";

/** Which of the three screens is showing. */
export type Step = "address" | "contact" | "result";

/** The whole widget's state. In memory only: no localStorage, no sessionStorage (ADR-0010). */
export type WidgetState = {
  submissionId: string;
  step: Step;
  address: string;
  token: string | null;
  /**
   * The outcomes that stop the flow at step 1, which is why this is named for
   * stopping rather than for the lookup. The three deferred reasons carry a
   * token and continue to step 2, so they are never held here: nothing at step 1
   * should be able to render them, and a field they cannot be assigned to is a
   * stronger guarantee of that than a comment.
   *
   * The reason union is spelled out rather than widened to `UnresolvedReason`,
   * which would let a deferred reason be assigned here and lose exactly the
   * guarantee described above.
   */
  blocked: { reason: "out_of_state" | "outside_service_area"; message: string } | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  contactConsent: boolean;
  verdict: DacVerdict | null;
  busy: boolean;
  error: string | null;
};

/**
 * In-memory only. No localStorage, no sessionStorage, no cookie.
 *
 * A partly-filled contact form left on a shared or kiosk browser must not be
 * recoverable by the next person, and there is nothing here worth the risk of
 * persisting: the flow is three screens long and re-entering an address costs
 * seconds.
 */
export function initialState(): WidgetState {
  return {
    submissionId: crypto.randomUUID(),
    step: "address",
    address: "",
    token: null,
    blocked: null,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    contactConsent: false,
    verdict: null,
    busy: false,
    error: null,
  };
}
